import { MenuListData, ShopTblData } from "./model";
import apps from "./apps";

let imageHost = null;

// 이미지 소스: prod 프로필만 pikashop-prod S3, 그 외(devel/Local 등)는 pikashop-devel S3.
// → URL에 "Local"을 붙여도(non-prod) 이미지는 pikashop-devel S3에서 읽는다.
// 코어 빌더: thumb=true면 400px 썸네일(menu{id}_{ver}_t.jpg), false면 원본(1000px). imageVer<=0이면 no_image.
// 썸네일(_t)이 아직 없는 이미지는 S3 404 → 호출부(ThumbImage)가 원본으로 onError 폴백한다.
export const menuImageUri = (shopid: string, menuid: number, imageVer: number, thumb: boolean): { uri: string } => {
  if (imageHost == null) {
    imageHost = apps.profile == "prod" ?
         "https://pikashop-prod.s3.ap-southeast-2.amazonaws.com" :
         "https://pikashop-devel.s3.ap-southeast-2.amazonaws.com";
  }
  if (imageVer <= 0) return { uri: `${imageHost}/menu/no_image.jpg` };
  const suffix = thumb ? "_t" : "";
  return { uri: `${imageHost}/menu/${shopid}/menu${menuid}_${imageVer}${suffix}.jpg` };
};

// 상세 히어로 — 원본(1000px) 유지.
export const menuUri = (shop: ShopTblData, menu: MenuListData) =>
  menuImageUri(shop.shopid, menu.menuid, menu.imageVer, false);

// 리스트/카드 이미지 — 400px 썸네일(_t).
export const menuUriByShopid = (shopid: string, menu: MenuListData) =>
  menuImageUri(shopid, menu.menuid, menu.imageVer, true);

// 작은 썸네일(카트·주문 리스트 등) — 400px 썸네일(_t).
export const menuThumbUri = (shopid: string, menuid: number, imageVer: number): { uri: string } =>
  menuImageUri(shopid, menuid, imageVer, true);

/**
 * 소숫점 1자리에서 반올림
 */
export const fixPrice = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * 화면 표시용 금액 포맷 — 항상 "$0.00" 형태(소수점 2자리 고정).
 * null/undefined는 0으로 처리. 표시 전용이며 계산에는 사용하지 않는다.
 */
export const money = (n: number): string => "$" + Number(n ?? 0).toFixed(2);

/**
 * 공휴일 서차지 금액. tabletious Tx.calc() 의 BigDecimal HALF_UP 을 정확히 복제한다.
 *
 * 1) 요율: TxServiceImpl:211 이 pct/100 을 소수 2자리 HALF_UP → Math.round(pct)/100 과 동치
 *          (12.5% → 0.13. "정확히" 계산하면 tabletious 청구액과 갈라진다)
 * 2) 금액: Tx.java:151 이 subtotal × rate 를 소수 2자리 HALF_UP
 *
 * 부동소수로 곱하면 어긋난다(20.10 × 15% → JS 3.0149999... → 3.01, Java 3.02).
 * 센트 정수 연산으로 계산해 HALF_UP 과 일치시킨다.
 */
export const holidaySurchargeAmount = (subTotal: number, pct: number): number => {
  // 전역 isFinite()(의도적, Number.isFinite 아님): pct가 문자열로 오면 Number.isFinite("15")===false라
  // 서차지가 조용히 0이 되어버린다. 전역 isFinite는 문자열을 숫자로 강제변환 후 판정해 더 안전하다 —
  // "더 엄격하게"(Number.isFinite로) 바꾸지 말 것(final-findings 리뷰 결정).
  if (!isFinite(subTotal) || !isFinite(pct) || subTotal <= 0 || pct <= 0) return 0;
  const rateHundredths = Math.round(pct);        // 12.5 → 13  (= 0.13)
  const subCents = Math.round(subTotal * 100);   // 20.10 → 2010
  const amtCents = Math.round((subCents * rateHundredths) / 100);
  return amtCents / 100;
};

/** UrPay 로 넘길 금액 = 소계 + 공휴일 서차지. 카드수수료는 UrPay 가 여기에 얹는다. */
export const payableAmount = (subTotal: number, pct: number): number =>
  Math.round((subTotal + holidaySurchargeAmount(subTotal, pct)) * 100) / 100;

// 매장별 스플래시 로고. shoplogo는 pikashop-prod 버킷에만 업로드되므로 프로필과 무관하게 prod에서 읽는다.
// S3에 해당 {shopid}.png가 없으면(404) 호출부에서 번들 로고로 폴백한다.
// 매장별 스플래시 로고. cacheBust 값을 쿼리로 붙여 로고 교체 시 캐시(브라우저/S3)를 무효화한다.
// 스플래시는 shop 설정(logoImageVer) 로드 전에 렌더되므로, 호출부에서 마운트 시각을 넘겨
// 스캔마다 새 URL이 되게 한다(→ 로고를 S3에서 바꾸면 즉시 최신).
export const shopLogoUri = (shopid: string, cacheBust: string | number = 1): { uri: string } =>
  ({ uri: `https://pikashop-prod.s3.ap-southeast-2.amazonaws.com/shoplogo/${shopid}.png?v=${cacheBust}` });

// 주류 카테고리 법적 경고 이미지: pikashop-prod의 menu/alcohol-{state}.jpg (모든 주 이미지가 prod에만 있으므로
// 스플래시 로고처럼 프로필과 무관하게 prod에서 읽는다). state 예: "NSW"→alcohol-nsw.jpg.
export const alcoholWarningUri = (state: string): { uri: string } =>
  ({ uri: `https://pikashop-prod.s3.ap-southeast-2.amazonaws.com/menu/alcohol-${state.toLowerCase()}.jpg` });
